import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { createColumnHelper } from "@tanstack/react-table";
import { defaultDateRange, useAuthConfig } from "@theprelude/core";
import { z } from "zod";
import {
  authorColumn,
  benchmarkColumn,
  createdColumn,
  defaultHidden,
  expectedResultColumn,
  getColumnVisibility,
  odpColumns,
  scheduledColumn,
  supportedPlatformsColumn,
  techniqueColumn,
  testActionsColumn,
  testIdColumn,
  testNameColumn,
  testProtectedColumn,
  threatsColumn,
} from "@/components/table/columns";
import { useTable } from "@/components/table/hooks";
import { VirtualTable } from "@/components/table/virtual-table";
import { DashboardTest, dashboardTestsQuery } from "@/hooks/quickview-queries";
import { ensureAuth } from "@/lib/auth";
import { authorizeFeatures } from "@/lib/authorize-feature";
import { getActivityQuery } from "@/lib/validators";

export const Route = createFileRoute("/_app/dashboard/tests")({
  validateSearch: z.object({
    search: z.string().optional(),
  }),
  loaderDeps: ({ search }) => {
    return {
      query: getActivityQuery(),
      search: search.search,
    };
  },
  beforeLoad: async (opts) => {
    await authorizeFeatures(opts.context, ["detect"]);
  },
  loader: async (opts) => {
    return opts.context.queryClient.ensureQueryData(
      dashboardTestsQuery(await ensureAuth(opts.context.auth), opts.deps.query),
    );
  },
  component: TestsPage,
});

const columnHelper = createColumnHelper<DashboardTest>();

const columns = [
  columnHelper.accessor("testId", defaultHidden(testIdColumn)),
  columnHelper.accessor("name", testNameColumn),
  columnHelper.accessor("technique", techniqueColumn),
  columnHelper.accessor("threatsDisplay", threatsColumn),
  columnHelper.accessor("author", authorColumn),
  columnHelper.accessor("created", createdColumn),
  columnHelper.accessor("supported_platforms", supportedPlatformsColumn),
  columnHelper.accessor("protected", testProtectedColumn),
  ...odpColumns(columnHelper),
  columnHelper.accessor("expectedResult", expectedResultColumn),
  columnHelper.accessor("benchmark", defaultHidden(benchmarkColumn)),
  columnHelper.accessor("status", scheduledColumn),
  columnHelper.accessor("testId", testActionsColumn),
];

function TestsPage() {
  const query = {
    start: defaultDateRange.start.toISOString(),
    finish: defaultDateRange.finish.toISOString(),
  };
  const authConfig = useAuthConfig();
  const testsTable = useSuspenseQuery(dashboardTestsQuery(authConfig, query));

  const params = Route.useLoaderDeps();

  const table = useTable({
    name: "dashboard-tests",
    data: testsTable.data ?? [],
    columns,
    initialState: {
      sorting: [{ id: createdColumn.id, desc: true }],
      columnVisibility: getColumnVisibility(columns),
      globalFilter: params.search,
    },
  });

  return <VirtualTable size="large" showFilter table={table} />;
}
